import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material'
import { useJsApiLoader } from '@react-google-maps/api'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import {
  LayoutFormat,
  LayoutButtons,
  Filter,
  PaginationButtons,
  PaginationContext,
  Loader,
  HelperWrapper,
} from 'src/components'
import { ScansTable } from './scans-table'
import { ScansGrid } from './scans-grid'
import {
  usePagination,
  useScanAccountsSelectionOptions,
  useScans,
  useMe,
  useAccount,
  useScanWithinGeoforceSelectionOptions,
  useScanTypeSelectionOptions,
  useScanStatusSelectionOptions,
  useScansStatusCount,
} from 'src/hooks'

import { countOffset, isUserHaveOnlyScannerRoleForAccounts, sortToObject } from 'src/common/utils'
import { useFilter } from 'src/hooks'
import { defaultPage, defaultLimit, defaultSort, sortOptions, scansChecboxOptions } from './filter'
import { EAccountRoles, ETranslationNs } from 'src/types'
import { jsApiLoaderOptions } from 'src/constants'
import { ScansStatusInfo } from './scans-status-info'

export const ScansList = () => {
  const { accountId } = useParams()
  const [searchParams] = useSearchParams()
  const isFilterOpened = searchParams.get('open')
  const { data: user, isLoading: isUserLoading } = useMe()
  const { data: account, isLoading: isAccountLoading } = useAccount()

  const location = useLocation()
  const isFromCreateScanPage = useRef<boolean>((location.state && location.state.from) || false)

  const [t] = useTranslation(ETranslationNs.Scans)
  const initialFilter = {
    query: '',
    accountsIds: accountId && accountId !== '' ? [accountId] : ['all'],
    status: ['all'],
    geofence: ['all'],
    scan_type: ['all'],
  }
  const [filter, setFilter] = useFilter({ initialState: initialFilter })

  const [isScans, setIsScans] = useState(true)
  const [sort, setSort] = useState(defaultSort)
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(defaultPage, defaultLimit)
  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop_l'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const [layoutFormat, setLayoutFormat] = useState<LayoutFormat>(
    isLaptop ? LayoutFormat.Grid : LayoutFormat.Table,
  )
  const {
    data,
    refetch: refetchScans,
    isLoading,
  } = useScans({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  let isUserScannerForAllAccounts = false
  let isScanerForCurrentAccount = false

  if (user) {
    isUserScannerForAllAccounts = isUserHaveOnlyScannerRoleForAccounts(user.accounts)
  }
  if (account) {
    isScanerForCurrentAccount = account.currentRoles.includes(EAccountRoles.scanner)
  }

  const isScanner =
    account && !isAccountLoading ? isScanerForCurrentAccount : isUserScannerForAllAccounts

  const { data: scanStatusCountData } = useScansStatusCount({
    filter: { accountsIds: (filter.accountsIds as string[]) || [] },
    isScanner,
  })

  const accountsSelectionOptions = useScanAccountsSelectionOptions(true)
  // const pushedSelectionOptions = useScanPushedSelectionOptions()
  // const reviewedSelectionOptions = useScanReviewedSelectionOptions()
  const statusSelectionOptions = useScanStatusSelectionOptions()
  const withinGeoforceSelectionOptions = useScanWithinGeoforceSelectionOptions()
  const scanTypeSelectionOptions = useScanTypeSelectionOptions()

  const sharedOptions = [
    accountsSelectionOptions,
    statusSelectionOptions,
    withinGeoforceSelectionOptions,
    scanTypeSelectionOptions,
  ]

  useEffect(() => {
    if (location.state?.from && !isLoading) {
      isFromCreateScanPage.current = !!location.state.from
    }
  }, [location, isLoading])

  useEffect(() => {
    refetchScans()
  }, [filter, sort, page, rowsPerPage, refetchScans])

  useEffect(() => {
    if (!isLoading && data) {
      if (data.data.length === 0) {
        setIsScans(false)
      }

      if (data.data.length > 0) {
        setIsScans(true)
      }
    }
  }, [isLoading, data])

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage, sort])

  useEffect(() => {
    const newLayoutFormat = isLaptop ? LayoutFormat.Grid : LayoutFormat.Table
    setLayoutFormat(newLayoutFormat)
  }, [isLaptop])

  const handleLayoutChange = () => setLayoutFormat(layoutFormat > 0 ? 0 : 1)

  const paginationValues = {
    count: data?.count ?? 0,
    page: page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!data && data.count > rowsPerPage,
  }

  const { isLoaded } = useJsApiLoader(jsApiLoaderOptions)

  return (
    <Grid container sx={{ flexDirection: 'column' }}>
      {!isUserLoading && (
        <Filter
          sort={sort}
          setSort={setSort}
          searchFieldText={t('scan-history-search-input.v')}
          filter={filter}
          setFilter={setFilter}
          selectionOptions={isScanner ? sharedOptions : [...sharedOptions]}
          sortOptions={sortOptions}
          checkboxOptions={isScanner ? undefined : scansChecboxOptions}
          initialState={initialFilter}
          isFilterOpened={isFilterOpened ? isFilterOpened === 'true' : true}
        />
      )}

      {isLoaded && (
        <PaginationContext.Provider value={paginationValues}>
          {!isLoading && (
            <ScansStatusInfo
              statusCountInfo={scanStatusCountData}
              filterName={'status'}
              defaultValue={['all']}
              setFilter={setFilter}
            />
          )}
          <LayoutButtons layoutFormat={layoutFormat} handleLayoutChange={handleLayoutChange} />
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                m: '30px',
              }}
            >
              <Loader isStatic />
            </Box>
          )}
          {!isLoading && !isScans && (
            <Box sx={{ maxWidth: '500px', fontSize: '14px', mt: '8px' }}>
              <HelperWrapper t="scan-history-empty-text" />
            </Box>
          )}
          {!isLoading &&
            isScans &&
            (layoutFormat === LayoutFormat.Table ? (
              <ScansTable
                sort={sort}
                setSort={setSort}
                data={data?.data}
                isScaner={isScanner}
                isFirstItemOpen={isFromCreateScanPage.current}
              />
            ) : (
              <ScansGrid
                data={data?.data}
                isScaner={isScanner}
                isFirstItemOpen={isFromCreateScanPage.current}
              />
            ))}
          {!isTablet && !isLoading && isScans && <PaginationButtons />}
        </PaginationContext.Provider>
      )}
    </Grid>
  )
}
